import { FC, useCallback } from "react";
import { useLocation } from "react-router-dom";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import {
  resolveCurrentGoogleAnalyticsClientIdFromBrowserCookie,
  resolveCurrentGoogleAnalyticsSessionIdFromBrowserCookie,
} from "@/modules/metrics/google-analytics.utils";
import { resolveSegmentAnonymousId } from "@/modules/metrics/segment.utils";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { windowModule } from "@/modules/window";
import { tryLocalDesktopCallbackHandoff } from "@/modules/window/local-desktop-callback";
import {
  buildDesktopStripeReturnDeepLink,
  type BrowserMetadataQueryParamValues,
} from "@/modules/subscriptions/desktop-return";
import { MDSLoadingIcon, MDSLoadingIconSize } from "@/mds/components/loading/loading-icon";
import { MDSText, MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds";

const LOG_PREFIX = "SubscriptionReturnDesktopPage";

export const SubscriptionReturnDesktopPage: FC = observer(() => {
  const location = useLocation();

  const resolveBrowserMetadata = useCallback(async (): Promise<BrowserMetadataQueryParamValues> => {
    logger.info({
      message: `[${LOG_PREFIX}][resolveBrowserMetadata] Resolving browser metadata.`,
    });

    const [segmentAnonymousId, gaClientId, gaSessionId] = await Promise.all([
      resolveSegmentAnonymousId().catch(() => null),
      resolveCurrentGoogleAnalyticsClientIdFromBrowserCookie().catch(() => null),
      resolveCurrentGoogleAnalyticsSessionIdFromBrowserCookie().catch(() => null),
    ]);

    logger.info({
      message: `[${LOG_PREFIX}][resolveBrowserMetadata] Browser metadata resolved.`,
      info: objectModule.safeAsJson({ segmentAnonymousId, gaClientId, gaSessionId }),
    });

    return {
      segmentAnonymousId,
      gaClientId,
      gaSessionId,
    };
  }, []);

  const navigateToTargetUrl = useCallback(async () => {
    const targetUrl = buildDesktopStripeReturnDeepLink({
      browserMetadata: await resolveBrowserMetadata(),
      search: location.search,
    });

    if (!targetUrl) {
      logger.error({
        message: `[${LOG_PREFIX}] Missing or invalid desktop return path.`,
        info: { search: location.search },
      });
      return;
    }

    const didHandoffToLocalDesktop = await tryLocalDesktopCallbackHandoff({ url: targetUrl });
    if (didHandoffToLocalDesktop) {
      return;
    }

    await windowModule.navigateToExternal({ url: targetUrl });
  }, [location.search, resolveBrowserMetadata]);

  useEffectOnMount(() => {
    void navigateToTargetUrl();
  });

  return (
    <Wrapper>
      <MDSLoadingIcon size={MDSLoadingIconSize.Large} />
      <LoadingMessage
        variant={MDSTextVariant.HeaderM}
        weight={MDSTextWeight.Regular}
        color={MDSTextColor.Contrast}
      >
        Opening Mem...
      </LoadingMessage>
      <MDSText
        variant={MDSTextVariant.TextS}
        weight={MDSTextWeight.Regular}
        color={MDSTextColor.Secondary}
      >
        Click to <StyledLink onClick={() => void navigateToTargetUrl()}>open</StyledLink> if nothing
        happens
      </MDSText>
    </Wrapper>
  );
});

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  flex-wrap: nowrap;
  background-color: ${({ theme }) => theme.colors.surface.chrome};
`;

const LoadingMessage = styled(MDSText)`
  margin-top: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const StyledLink = styled.span`
  text-decoration: underline;
  cursor: pointer;
`;
